import { Controller } from "@hotwired/stimulus";

/**
 * Addon Controller
 *
 * Responsibilities:
 * • Handle badge toggles for all plan categories
 * • Sync modal switches/quantities with closed-card summaries
 * • Update badge states when modal inputs change
 * • Notify pricing controller when addon selections change
 */

export default class extends Controller {
  static BADGE_INACTIVE_CLASS = "wg-chip-gray-outline";
  static BADGES_LIST_SELECTOR = "[data-pricing-target='badgesList']";
  static targets = ["modal", "switchInput", "quantityInput"];

  connect() {
    WGModal.init();
    CloseableGroup.init();

    this._onChangeOrInput = (e) => {
      if (e.target.matches(".switch-wrapper input[type=\"checkbox\"]")) {
        this.updateClosedSwitchFromInput(e.target);
      } else if (e.target.matches(".quantity-input")) {
        this.updateClosedQuantityFromInput(e.target);
      }
    };

    if (this.hasModalTarget) {
      this.modalTarget.addEventListener("change", this._onChangeOrInput);
      this.modalTarget.addEventListener("input", this._onChangeOrInput);

      // Initial sync so closed state matches current input state
      this.initialSync();
    }
  }

  disconnect() {
    if (this.hasModalTarget) {
      this.modalTarget.removeEventListener("change", this._onChangeOrInput);
      this.modalTarget.removeEventListener("input", this._onChangeOrInput);
    }
  }

  toggleFromBadge(event) {
    const badge = event.currentTarget;
    const key = badge.dataset.badgeKey;
    const allowsMultiple = badge.dataset.multiple === "true";

    const badgesList = badge.closest(this.constructor.BADGES_LIST_SELECTOR);
    if (!badgesList) return;

    const planCategory = badgesList.dataset.badgePlanType;

    if (allowsMultiple) {
      // Quantity addons: toggle between 0 and 1, open modal for more
      const willBeActive = badge.classList.contains(this.constructor.BADGE_INACTIVE_CLASS);
      const nextQuantity = willBeActive ? 1 : 0;
      this.setQuantityAndNotify(key, nextQuantity, planCategory);
      return;
    }

    // Switch addons: toggle on/off
    const nowActive = this.toggleBadgeVisual(badge);

    const checkbox = this.findAddonInput(key, "checkbox");
    if (checkbox && checkbox.checked !== nowActive) {
      checkbox.checked = nowActive;
      checkbox.dispatchEvent(new Event("change", { bubbles: true }));
    } else {
      this.dispatchSelectionChanged();
    }
  }

  toggleBadgeVisual(badge) {
    badge.classList.toggle(this.constructor.BADGE_INACTIVE_CLASS);
    return !badge.classList.contains(this.constructor.BADGE_INACTIVE_CLASS);
  }

  continue() { WGModal.close("#pricing-addon-modal"); }

  initialSync() {
    if (this.hasSwitchInputTarget) {
      this.switchInputTargets.forEach((input) => this.updateClosedSwitchFromInput(input, { notify: false }));
    }

    if (this.hasQuantityInputTarget) {
      this.quantityInputTargets.forEach((input) => this.updateClosedQuantityFromInput(input, { notify: false }));
    }

    // No pricing notification on initial load; pricing controller handles first refresh
  }

  findAddonInput(key, type) {
    if (!this.hasModalTarget) return null;

    if (type === "checkbox") {
      return this.modalTarget.querySelector(`.switch-wrapper input[type="checkbox"][name="${key}"]`);
    } else if (type === "quantity") {
      return this.modalTarget.querySelector(`.quantity-input[name="${key}"]`);
    }
    return null;
  }

  updateBadges(key, callback) {
    const currentPlanCategory = this.hasModalTarget ? this.modalTarget.dataset.currentPlanCategory : null;

    if (currentPlanCategory) {
      // Update only the badges for the plan that opened the modal
      const badgesList = document.querySelector(
        `${this.constructor.BADGES_LIST_SELECTOR}[data-badge-plan-type="${currentPlanCategory}"]`
      );
      if (badgesList) {
        badgesList.querySelectorAll(`[data-badge-key="${key}"]`).forEach(callback);
      }
    } else {
      // No category set: update all badge lists
      const badgeLists = document.querySelectorAll(this.constructor.BADGES_LIST_SELECTOR);
      badgeLists.forEach(list => {
        list.querySelectorAll(`[data-badge-key="${key}"]`).forEach(callback);
      });
    }
  }

  setQuantityAndNotify(key, quantity, planCategory = null) {
    const input = this.findAddonInput(key, "quantity");
    if (!input) return;

    // Store which plan triggered this change
    if (planCategory && this.hasModalTarget) {
      this.modalTarget.dataset.currentPlanCategory = planCategory;
    }

    input.value = quantity;
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  updateClosedSwitchFromInput(input, options = {}) {
    const key = input.name;
    if (!this.hasModalTarget) return;

    const closedCard = this.modalTarget.querySelector(`.group-closed-content .closed-card[data-setting="${key}"]`);
    if (closedCard) {
      closedCard.classList.remove("switch-on", "switch-off");
      closedCard.classList.add(input.checked ? "switch-on" : "switch-off");
    }

    const addonRow = input.closest(".addon-option");
    if (addonRow) {
      addonRow.classList.toggle("active", !!input.checked);
    }

    // Keep badges in sync with modal switches
    this.updateBadges(key, badge => {
      badge.classList.toggle(this.constructor.BADGE_INACTIVE_CLASS, !input.checked);
    });

    this.dispatchSelectionChanged(options);
  }

  updateClosedQuantityFromInput(input, options = {}) {
    const key = input.name;
    const value = input.value ?? "";
    if (!this.hasModalTarget) return;

    const targetSpan = this.modalTarget.querySelector(`#addon-${key}`);
    if (targetSpan) {
      targetSpan.textContent = value;
    }

    const addonRow = input.closest(".addon-option");
    if (addonRow) {
      addonRow.classList.toggle("active", (parseFloat(value) || 0) > 0);
    }

    const quantity = parseInt(value, 10) || 0;
    this.updateBadges(key, badge => {
      const shouldBeInactive = quantity <= 0;
      badge.classList.toggle(this.constructor.BADGE_INACTIVE_CLASS, shouldBeInactive);
    });

    if (options.notify === false) return;
    this.dispatchSelectionChanged(options);
  }

  dispatchSelectionChanged(options = {}) {
    if (options.notify === false) return;
    this.dispatch("selection-changed", { bubbles: true });
  }
}
